import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import { useTheme } from '../context/ThemeContext';
import BrandWordmark from './BrandWordmark';
import {
  getActiveMainTab,
  getActiveDashboardStackRoute,
  isDashboardStackScreen,
  type MainTabRoute,
} from '../navigation/webNavigation';

type TabNav = BottomTabNavigationProp<Record<MainTabRoute, undefined>>;

type NavItem = {
  route: MainTabRoute;
  label: string;
  icon: React.ComponentProps<typeof Ionicons>['name'];
  iconFocused: React.ComponentProps<typeof Ionicons>['name'];
};

const NAV_ITEMS: NavItem[] = [
  { route: 'Dashboard', label: 'Dashboard', icon: 'home-outline', iconFocused: 'home' },
  { route: 'Goals', label: 'My Goals', icon: 'flag-outline', iconFocused: 'flag' },
  { route: 'Income', label: 'Income & Allocation', icon: 'wallet-outline', iconFocused: 'wallet' },
  { route: 'Progress', label: 'Progress', icon: 'stats-chart-outline', iconFocused: 'stats-chart' },
  { route: 'Report', label: 'Report', icon: 'document-text-outline', iconFocused: 'document-text' },
];

const WebSidebar = () => {
  const { colors } = useTheme();
  const navigation = useNavigation<TabNav>();
  const navState = useNavigationState(state => state);
  const activeTab = getActiveMainTab(navState ?? undefined);
  const onDashboardSubScreen = isDashboardStackScreen(navState ?? undefined);
  const settingsFocused =
    getActiveDashboardStackRoute(navState ?? undefined) === 'Settings';

  const openSettings = () => {
    navigation.navigate('Dashboard', { screen: 'Settings' } as never);
  };

  return (
    <View style={[styles.sidebar, { backgroundColor: colors.nav.background, borderRightColor: colors.nav.border }]}>
      <View style={[styles.brandBlock, { borderBottomColor: colors.nav.border }]}>
        <BrandWordmark variant="light" size="md" />
        <Text style={[styles.brandTagline, { color: colors.nav.textMuted }]}>Smart Financial Goals</Text>
      </View>

      <View style={styles.navSection}>
        {NAV_ITEMS.map(item => {
          const focused =
            activeTab === item.route &&
            !(item.route === 'Dashboard' && onDashboardSubScreen);
          return (
            <TouchableOpacity
              key={item.route}
              style={[
                styles.navItem,
                focused && { backgroundColor: colors.nav.activeBackground },
              ]}
              onPress={() => navigation.navigate(item.route)}
              accessibilityRole="button"
              accessibilityState={{ selected: focused }}
            >
              <Ionicons
                name={focused ? item.iconFocused : item.icon}
                size={22}
                color={focused ? colors.nav.activeText : colors.nav.text}
              />
              <Text
                style={[
                  styles.navLabel,
                  { color: colors.nav.text },
                  focused && { color: colors.nav.activeText, fontWeight: '600' },
                ]}
              >
                {item.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <TouchableOpacity
        style={[
          styles.settingsItem,
          { borderTopColor: colors.nav.border },
          settingsFocused && { backgroundColor: colors.nav.activeBackground },
        ]}
        onPress={openSettings}
        accessibilityRole="button"
        accessibilityState={{ selected: settingsFocused }}
      >
        <Ionicons
          name={settingsFocused ? 'settings' : 'settings-outline'}
          size={22}
          color={settingsFocused ? colors.nav.activeText : colors.nav.text}
        />
        <Text
          style={[
            styles.settingsLabel,
            { color: colors.nav.text },
            settingsFocused && { color: colors.nav.activeText, fontWeight: '600' },
          ]}
        >
          Settings
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const SIDEBAR_WIDTH = 260;

const styles = StyleSheet.create({
  sidebar: {
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    paddingTop: Platform.OS === 'web' ? 28 : 24,
    paddingBottom: 24,
    paddingHorizontal: 16,
    borderRightWidth: 1,
    ...(Platform.OS === 'web' ? { minHeight: '100vh' as unknown as number } : {}),
  },
  brandBlock: {
    paddingHorizontal: 8,
    paddingBottom: 28,
    borderBottomWidth: 1,
    marginBottom: 16,
  },
  brandTagline: {
    fontSize: 13,
    marginTop: 4,
  },
  navSection: {
    flex: 1,
    gap: 6,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
  },
  navLabel: {
    fontSize: 15,
    fontWeight: '500',
    flex: 1,
  },
  settingsItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderTopWidth: 1,
    marginTop: 8,
    paddingTop: 20,
  },
  settingsLabel: {
    fontSize: 15,
    fontWeight: '500',
  },
});

export { SIDEBAR_WIDTH };
export default WebSidebar;
